import * as React from 'react'

import axios from 'axios'

export interface EnvironmentResponse {
  api_endpoint: string
  api_realtime_endpoint: string
  factorial_id_endpoint: string
  webpage_location: string
  last_release_version: string
  broken_versions: string[]
  broken_periods: Array<[string, string]>
  sentry_enabled: boolean
  sentry_environment: string
  amplitude_enabled: boolean
  amplitude_dev_logs_enabled: boolean
  datadog_logs_enabled: boolean
  datadog_rum_enabled: boolean
  datadog_rum_allowed_company_ids: number[]
  datadog_environment: string
  clarity_enabled: boolean
  devtools_enabled: boolean
  google_maps_api_key: string
  intercom_api_id: string
  intercom_api_base: string
  hapi_elements_url: string
  facebook_pixel_enabled: boolean
  deployment_phase: string
  skip_animations: boolean
  developer_english_enabled: boolean
  capture_explicit_sentry_errors: boolean
  ignore_upsell: boolean
  show_onboarding_first_steps: boolean
  show_intercom_chat: boolean
  hide_product_announcements: boolean
  hide_clock_in_popup: boolean
  hide_org_chart_banner_and_hint: boolean
  hide_jobs_catalog_popup: boolean
  skip_get_site_control: boolean
  hide_project_upsell_clients_modal: boolean
  hide_competencies_stand_alone_pages: boolean
  nuvo_api_key: string
  nuvo_developer_mode: boolean
  graphql_persisted_queries_mode: string
  mastra_location: string
  preload_demo_login: boolean
  show_errors_in_error_boundary: boolean
}

export interface Environment {
  apiEndpoint: string
  apiRealtimeEndpoint: string
  factorialIdEndpoint: string
  webpageLocation: string
  releaseVersion: string
  releaseDate: string
  lastReleaseVersion: string
  brokenVersions: string[]
  brokenPeriods: Array<[string, string]>
  sentryEnabled: boolean
  sentryEnvironment: string
  amplitudeEnabled: boolean
  amplitudeDevLogsEnabled: boolean
  datadogLogsEnabled: boolean
  datadogRumEnabled: boolean
  datadogRumAllowedCompanyIds: number[]
  datadogEnvironment: string
  clarityEnabled: boolean
  devtoolsEnabled: boolean
  googleMapsApiKey: string
  intercomApiId: string
  intercomApiBase: string
  hapiElementsUrl: string
  facebookPixelEnabled: boolean
  deploymentPhase: string
  developerEnglishEnabled: boolean
  captureExplicitSentryErrors: boolean
  ignoreUpsell: boolean
  showOnboardingFirstSteps: boolean
  showIntercomChat: boolean
  hideProductAnnouncements: boolean
  hideOrgChartBannerAndHint: boolean
  hideJobsCatalogPopup: boolean
  skipGetSiteControl: boolean
  hideProjectUpsellClientsModal: boolean
  nuvoApiKey: string
  nuvoDeveloperMode: boolean
  graphqlPersistedQueriesMode: string
  mastraLocation: string
  preloadDemoLogin: boolean
  showErrorsinErrorBoundary: boolean
}

export const fetchEnvironment = async (): Promise<Environment> => {
  const { data } = await axios.get<EnvironmentResponse>('/environment.json', {
    params: { ts: new Date().getTime() }, // Force fresh data
  })

  const {
    api_endpoint: apiEndpoint,
    api_realtime_endpoint: apiRealtimeEndpoint,
    factorial_id_endpoint: factorialIdEndpoint,
    webpage_location: webpageLocation,
    last_release_version: lastReleaseVersion,
    broken_versions: brokenVersions,
    broken_periods: brokenPeriods,
    sentry_enabled: sentryEnabled,
    sentry_environment: sentryEnvironment,
    amplitude_enabled: amplitudeEnabled,
    amplitude_dev_logs_enabled: amplitudeDevLogsEnabled,
    datadog_logs_enabled: datadogLogsEnabled,
    datadog_rum_enabled: datadogRumEnabled,
    datadog_rum_allowed_company_ids: datadogRumAllowedCompanyIds,
    datadog_environment: datadogEnvironment,
    clarity_enabled: clarityEnabled,
    devtools_enabled: devtoolsEnabled,
    google_maps_api_key: googleMapsApiKey,
    intercom_api_id: intercomApiId,
    intercom_api_base: intercomApiBase,
    hapi_elements_url: hapiElementsUrl,
    facebook_pixel_enabled: facebookPixelEnabled,
    deployment_phase: deploymentPhase,
    developer_english_enabled: developerEnglishEnabled,
    capture_explicit_sentry_errors: captureExplicitSentryErrors,
    ignore_upsell: ignoreUpsell,
    show_onboarding_first_steps: showOnboardingFirstSteps,
    show_intercom_chat: showIntercomChat,
    hide_product_announcements: hideProductAnnouncements,
    hide_org_chart_banner_and_hint: hideOrgChartBannerAndHint,
    hide_jobs_catalog_popup: hideJobsCatalogPopup,
    skip_get_site_control: skipGetSiteControl,
    hide_project_upsell_clients_modal: hideProjectUpsellClientsModal,
    nuvo_api_key: nuvoApiKey,
    nuvo_developer_mode: nuvoDeveloperMode,
    graphql_persisted_queries_mode: graphqlPersistedQueriesMode,
    mastra_location: mastraLocation,
    preload_demo_login: preloadDemoLogin,
    show_errors_in_error_boundary: showErrorsinErrorBoundary,
  } = data

  if (!apiEndpoint) {
    throw new Error(
      'FATAL: API endpoint is not initialized. Please check your environment settings.'
    )
  }

  return {
    apiEndpoint,
    apiRealtimeEndpoint,
    webpageLocation,
    releaseVersion: process.env.RELEASE_VERSION ?? 'unknown',
    releaseDate: process.env.RELEASE_DATE ?? 'unknown',
    brokenVersions,
    brokenPeriods,
    lastReleaseVersion,
    sentryEnabled,
    sentryEnvironment,
    amplitudeEnabled,
    amplitudeDevLogsEnabled,
    datadogLogsEnabled,
    datadogRumEnabled,
    datadogRumAllowedCompanyIds,
    datadogEnvironment,
    clarityEnabled,
    devtoolsEnabled,
    googleMapsApiKey,
    intercomApiId,
    intercomApiBase,
    hapiElementsUrl,
    facebookPixelEnabled,
    deploymentPhase,
    developerEnglishEnabled,
    captureExplicitSentryErrors,
    ignoreUpsell,
    showOnboardingFirstSteps,
    showIntercomChat,
    hideProductAnnouncements,
    hideOrgChartBannerAndHint,
    hideJobsCatalogPopup,
    skipGetSiteControl,
    hideProjectUpsellClientsModal,
    nuvoApiKey,
    nuvoDeveloperMode,
    graphqlPersistedQueriesMode,
    mastraLocation,
    preloadDemoLogin,
    showErrorsinErrorBoundary,
    factorialIdEndpoint,
  }
}

export const defaultEnvironment: Environment = {
  apiEndpoint: 'not_initialized',
  apiRealtimeEndpoint: 'not_initialized',
  factorialIdEndpoint: 'not_initialized',
  webpageLocation: 'not_initialized',
  releaseVersion: 'not_initialized',
  releaseDate: 'not_initialized',
  lastReleaseVersion: 'not_initialized',
  brokenVersions: [],
  brokenPeriods: [],
  sentryEnabled: false,
  sentryEnvironment: 'not_initialized',
  amplitudeEnabled: false,
  amplitudeDevLogsEnabled: false,
  datadogLogsEnabled: false,
  datadogRumEnabled: false,
  datadogRumAllowedCompanyIds: [],
  datadogEnvironment: 'not_initialized',
  clarityEnabled: false,
  devtoolsEnabled: false,
  googleMapsApiKey: 'not_initialized',
  intercomApiId: 'not_initialized',
  intercomApiBase: 'not_initialized',
  hapiElementsUrl: 'not_initialized',
  facebookPixelEnabled: false,
  deploymentPhase: 'not_initialized',
  developerEnglishEnabled: false,
  captureExplicitSentryErrors: false,
  ignoreUpsell: false,
  showOnboardingFirstSteps: false,
  showIntercomChat: false,
  hideProductAnnouncements: false,
  hideOrgChartBannerAndHint: false,
  hideJobsCatalogPopup: false,
  skipGetSiteControl: false,
  hideProjectUpsellClientsModal: false,
  nuvoApiKey: 'not_initialized',
  nuvoDeveloperMode: false,
  graphqlPersistedQueriesMode: 'not_initialized',
  mastraLocation: 'not_initialized',
  preloadDemoLogin: false,
  showErrorsinErrorBoundary: false,
}

export const EnvironmentContext = React.createContext<Environment>(defaultEnvironment)
